import { css } from '@emotion/react';
import Color from '../../../../../util/color';
import Select from 'components/form/select';
import Label from 'components/_shared/elements/label';
import mem from 'mem';
import {
  ChangeEvent,
  FormEvent,
  useCallback,
  useMemo,
  useState,
  KeyboardEvent,
  ReactNode,
} from 'react';
import {
  isSelect,
  isSelectable,
  isText,
  isTextArea,
} from 'types/guards/customizable-options';
import {
  AddToCartCustomOption,
  Category,
  CustomizableOptionInterface,
  CustomizableOptionValueInterface,
  CustomizableProductInterface,
  CustomizableTextAreaOption,
} from 'types/types';
import LabeledInput from 'components/form/input';
import { Box, Flex, Grid } from 'components/box';
import TextArea from 'components/form/text-area';
import isEmail from 'sane-email-validation';
import RichText from 'components/rich-text';
import { trackSizeGuideInteraction } from '../../../../../util/tag-manager';
import { EventNamesEnum } from 'types/third-party/gtm';
import RulerIcon from 'icons/ruler.svg';
import {
  InformationButton,
  InformationLink,
} from 'components/_pages/products/buttons/informational-button';
import SizePill from './size-pill';
import { isCustomizableOptionWithValues } from 'types/guards/product';

interface SelectedOptions {
  [optionId: string]: {
    valueId: string;
    parentIds: string[];
  };
}

/**
 * TODO: Adding this horrid little thing here since we don't have
 * types on custom options. Need to update once we can get
 * this from the API
 */
const isValidEmail = (option, value: string) => {
  return option.label.toLowerCase().match('email')
    ? isEmail(value)
      ? true
      : false
    : true;
};

/**
 * When looking at an option, the values inside of the option can each define parents that they're dependent on.
 * If ANY value in an option has ANY of it's parents in the selected list: we must show the option.
 */
const isParentSelected = mem(
  (option: CustomizableOptionInterface, selectedOptions: SelectedOptions) => {
    if (!isSelectable(option)) return false;

    /**
     * Look for a selected option that is the parent of any of the values in the current option.
     */
    return Object.values(selectedOptions).some(selectedOption =>
      option.values.some(value =>
        (value.dependency?.parents || []).some(parent => {
          return parent === selectedOption.valueId;
        })
      )
    );
  },
  {
    cacheKey: ([option, selectedOptions]) =>
      `${option.id}.${JSON.stringify(selectedOptions)}`,
  }
);

const updateChildSelectionsRecursive = (
  selectedOptions: SelectedOptions,
  depth = 0
): SelectedOptions => {
  /**
   * it's unlikely we'll ever have products with more than 5 options in the hierarchy
   * so this is just a safety net.
   */
  if (depth > 5) {
    return selectedOptions;
  }

  const selected: (string | null)[] = [];
  Object.values(selectedOptions).map(selection => {
    selected.push(selection.valueId);
  });

  Object.entries(selectedOptions).map(([id, selection]) => {
    // TODO: if/when we support AND groupings, we can conditionally use Array.some/every
    const remove =
      !selection.valueId ||
      (selection.parentIds.length > 0 &&
        !selection.parentIds.some(p => selected.indexOf(p) !== -1));
    if (remove) {
      delete selectedOptions[id];
      selectedOptions = updateChildSelectionsRecursive(
        selectedOptions,
        ++depth
      );
    }
  });

  return selectedOptions;
};

const PILL_MAX_LABEL_LENGTH = 9;

const PILL_CATEGORY_NAMES = ['apparel & accessories', 'shoes'];

const hasShortLabels = (option: CustomizableOptionInterface) =>
  isCustomizableOptionWithValues(option) &&
  option.values.every(size => size.label.length <= PILL_MAX_LABEL_LENGTH);

const shouldUsePillSizeSelection = (
  option: CustomizableOptionInterface,
  categories?: Category[]
) => {
  const labelAsLowerCase = option.label.toLowerCase();
  const isSizeOption =
    labelAsLowerCase === 'size' || labelAsLowerCase.startsWith('size ');

  return (
    isSizeOption &&
    hasShortLabels(option) &&
    categories?.some(category =>
      PILL_CATEGORY_NAMES.includes(category.name.toLowerCase())
    )
  );
};

const CustomizableOption = ({
  option,
  selectedOptions,
  select,
  isAvailable,
  isHighlighted,
  inputText,
  showValidationErrors,
  disabled,
  additionalInfo,
  actions,
  categories,
}: {
  option: CustomizableOptionInterface;
  selectedOptions: SelectedOptions;
  select: (
    option: CustomizableOptionInterface,
    value?: CustomizableOptionValueInterface
  ) => void;
  isAvailable: boolean;
  isHighlighted?: boolean;
  inputText?: (option: CustomizableTextAreaOption, value: string) => void;
  showValidationErrors?: boolean;
  disabled?: boolean;
  additionalInfo?: string;
  actions?: ReactNode;
  categories?: Category[];
}) => {
  const isEmailField = option.label.toLowerCase().includes('email');

  const isPillSizeOption = useMemo(
    () => shouldUsePillSizeSelection(option, categories),
    [option, categories]
  );

  return (
    <>
      {isSelect(option) && isAvailable && (
        <Box width="100%">
          <Grid
            width="100%"
            gridTemplateColumns={actions ? '1fr auto' : '1fr'}
            gridRowGap={0}
            gridTemplateAreas={
              actions && additionalInfo
                ? `'label label' 'info actions'`
                : actions && !additionalInfo
                ? `'label actions'`
                : !actions && additionalInfo
                ? `'label' 'info'`
                : `'label'`
            }
          >
            <Box gridArea="label">
              <Label
                fontWeight={600}
                lineHeight={1.5}
                mb={1}
                css={css`
                  overflow: hidden;
                  text-overflow: ellipsis;
                  --max-lines: 1;
                  white-space: nowrap;
                `}
              >
                {option.label}
              </Label>
            </Box>

            {additionalInfo && (
              <Box gridArea="info">
                <RichText
                  fontWeight="semiBold"
                  mb="8px"
                  color="purple"
                  fontSize="12px"
                  content={additionalInfo}
                />
              </Box>
            )}

            {actions && (
              <Flex
                justifyContent="flex-end"
                alignSelf="end"
                gridArea="actions"
              >
                {actions}
              </Flex>
            )}
          </Grid>

          {isPillSizeOption ? (
            <SizePill
              option={option}
              selectedValueId={selectedOptions?.[option.id]?.valueId}
              select={select}
              isHighlighted={isHighlighted}
            />
          ) : (
            <Select
              name={option.id}
              disabled={disabled}
              onChange={(
                event: ChangeEvent<HTMLSelectElement> &
                  FormEvent<HTMLLabelElement>
              ) =>
                select(
                  option,
                  option.values.find(v => v.id === event.target.value)
                )
              }
              css={theme => css`
                overflow: hidden;
                text-overflow: ellipsis;
                ${isHighlighted &&
                css`
                  box-shadow: 0 0 3px 4px
                    ${Color(theme.colors.danger).alpha(0.3).rgb().string()};
                  border-color: ${theme.colors.danger};
                `}
              `}
            >
              <option value="0">Please select...</option>
              {option.values.map(({ id, label, isSoldOut }) => (
                <option key={id} value={id} disabled={isSoldOut}>
                  {`${label}${isSoldOut ? ' (sold out)' : ''}`}
                </option>
              ))}
            </Select>
          )}
        </Box>
      )}

      {/*  Just removing isAvailable because the requirement to show or hide option based
      on selecting email or print will return once the parent child dependency between selects
      and text fields/area has been fixed */}
      {isText(option) && (
        <Box mr="5px" gridColumn="1 / span 2">
          <Label
            fontWeight={600}
            lineHeight={1.5}
            mb={1}
            css={css`
              overflow: hidden;
              text-overflow: ellipsis;
              --max-lines: 1;
              white-space: nowrap;
            `}
          >
            {option.label}
          </Label>

          <LabeledInput
            onKeyUp={(event: KeyboardEvent<HTMLInputElement>) =>
              inputText && inputText(option, event.currentTarget.value)
            }
            name={option.id}
            placeholder={`Please enter ${option.label}`}
            error={
              showValidationErrors
                ? isEmailField
                  ? 'Please enter a valid email address'
                  : 'This field is required'
                : undefined
            }
            invalid={isHighlighted}
            type={isEmailField ? 'email' : 'text'}
            css={theme => css`
              overflow: hidden;
              text-overflow: ellipsis;
              ${isHighlighted &&
              css`
                box-shadow: 0 0 3px 4px
                  ${Color(theme.colors.danger).alpha(0.3).rgb().string()};
                border-color: ${theme.colors.danger};
              `}
            `}
            disabled={disabled}
          />
        </Box>
      )}

      {isTextArea(option) && (
        <Box mr="5px" gridColumn="1 / span 2">
          <Label
            fontWeight={600}
            lineHeight={1.5}
            mb={1}
            css={css`
              overflow: hidden;
              text-overflow: ellipsis;
              --max-lines: 1;
              white-space: nowrap;
            `}
          >
            {option.label}
          </Label>

          <TextArea
            onKeyUp={(event: KeyboardEvent<HTMLTextAreaElement>) =>
              inputText && inputText(option, event.currentTarget.value)
            }
            name={option.id}
            placeholder={`Please enter ${option.label}`}
            type="text"
            css={theme => css`
              overflow-y: scroll;
              ${isHighlighted &&
              css`
                box-shadow: 0 0 3px 4px
                  ${Color(theme.colors.danger).alpha(0.3).rgb().string()};
                border-color: ${theme.colors.danger};
              `}
            `}
            disabled={disabled}
          />
        </Box>
      )}
    </>
  );
};

const SizeOptionActions = ({
  openSizeGuide,
  productId,
}: {
  openSizeGuide?: () => void;
  productId: string;
}) => {
  if (openSizeGuide) {
    return (
      <InformationButton
        onClick={() => {
          openSizeGuide();
          trackSizeGuideInteraction(EventNamesEnum.openSizeGuide);
        }}
        iconSrc={RulerIcon}
        text="Size Guide"
        highlighted
        fontSize="12px"
      />
    );
  }

  return (
    <InformationLink
      iconSrc={RulerIcon}
      text="Size Guide"
      highlighted
      href={`/products/${productId}?openSizeGuide=true`}
      fontSize="12px"
    />
  );
};

const CustomOptionSelector = ({
  product,
  setCustomOptionsState,
  highlightEmptyOptions,
  disabled,
  openSizeGuide,
}: {
  product: CustomizableProductInterface;
  setCustomOptionsState?: (state: {
    areCustomOptionsValid: boolean;
    addToCartOptions: AddToCartCustomOption[];
    activeValue?: CustomizableOptionValueInterface;
  }) => void;
  highlightEmptyOptions?: boolean;
  disabled?: boolean;
  openSizeGuide?: () => void;
}) => {
  const [selectedOptions, setSelectedOptions] = useState<SelectedOptions>({});
  const [activeValue, setActiveValue] = useState<
    CustomizableOptionValueInterface | undefined
  >();

  const textUpdated = useCallback(
    (option: CustomizableTextAreaOption, value: string) => {
      let updatedSelectedOptions = selectedOptions;
      const maxLengthReached = option.maxLength
        ? value.length > option.maxLength
        : false;

      if (value && isValidEmail(option, value) && !maxLengthReached) {
        // update the selected options object with this new value
        updatedSelectedOptions = updateChildSelectionsRecursive({
          ...selectedOptions,
          [option.id]: {
            valueId: value,
            parentIds: [],
          },
        });
      } else {
        // if there was a selection for this option filter it out and rebuild the object.
        updatedSelectedOptions = updateChildSelectionsRecursive({
          ...Object.entries(selectedOptions)
            .filter(([selectedOption]) => selectedOption !== option.id)
            .reduce(
              (agg, [selectedOption, selectedValue]) => ({
                ...agg,
                [selectedOption]: selectedValue,
              }),
              {}
            ),
        });
      }
      setSelectedOptions(updatedSelectedOptions);

      const areCustomOptionsValid = product.customizableOptions.every(
        option =>
          !option.isRequired ||
          option.id in updatedSelectedOptions ||
          (option.isDependent &&
            !isParentSelected(option, updatedSelectedOptions))
      );

      setCustomOptionsState &&
        setCustomOptionsState({
          areCustomOptionsValid,
          addToCartOptions: Object.entries(
            updatedSelectedOptions
          ).map(([id, { valueId }]) => ({ id, value: valueId })),
          activeValue,
        });
    },
    [
      activeValue,
      product.customizableOptions,
      selectedOptions,
      setCustomOptionsState,
    ]
  );

  const selectOptionValue = useCallback(
    (
      option: CustomizableOptionInterface,
      value?: CustomizableOptionValueInterface
    ) => {
      let updatedSelectedOptions = selectedOptions;

      if (value) {
        // update the selected options object with this new value
        updatedSelectedOptions = updateChildSelectionsRecursive({
          ...selectedOptions,
          [option.id]: {
            valueId: value.id,
            parentIds: value.dependency?.parents || [],
          },
        });
      } else {
        // if there was a selection for this option filter it out and rebuild the object.
        updatedSelectedOptions = updateChildSelectionsRecursive({
          ...Object.entries(selectedOptions)
            .filter(([selectedOption]) => selectedOption !== option.id)
            .reduce(
              (agg, [selectedOption, selectedValue]) => ({
                ...agg,
                [selectedOption]: selectedValue,
              }),
              {}
            ),
        });
      }

      setSelectedOptions(updatedSelectedOptions);

      const areCustomOptionsValid = product.customizableOptions.every(
        option =>
          !option.isRequired ||
          option.id in updatedSelectedOptions ||
          (option.isDependent &&
            !isParentSelected(option, updatedSelectedOptions))
      );

      setActiveValue(value);
      setCustomOptionsState &&
        setCustomOptionsState({
          areCustomOptionsValid,
          addToCartOptions: Object.entries(
            updatedSelectedOptions
          ).map(([id, { valueId }]) => ({ id, value: valueId })),
          activeValue: value,
        });
    },
    [selectedOptions, product.customizableOptions, setCustomOptionsState]
  );

  return (
    <>
      {product.customizableOptions.map(option => {
        const labelAsLowerCase = option.label.toLowerCase();
        const isSizeOption =
          labelAsLowerCase === 'size' || labelAsLowerCase.startsWith('size ');

        const showSizeGuideActions =
          !!product.sizeChart?.desktopImage ||
          !!product.sizeChart?.howToMeasure;

        const sizeGuideProps = {
          additionalInfo: product.sizeChart?.recommendation,
          actions: showSizeGuideActions ? (
            <SizeOptionActions
              openSizeGuide={openSizeGuide}
              productId={product.id}
            />
          ) : undefined,
        };

        return (
          <CustomizableOption
            showValidationErrors={product.isGiftVoucher}
            key={option.id}
            option={option}
            selectedOptions={selectedOptions}
            select={selectOptionValue}
            inputText={textUpdated}
            isAvailable={
              !option.isDependent || isParentSelected(option, selectedOptions)
            }
            isHighlighted={
              highlightEmptyOptions &&
              option.isRequired &&
              !selectedOptions[option.id]
            }
            disabled={disabled}
            {...(isSizeOption ? sizeGuideProps : {})}
            categories={product.topLevelCategories}
          />
        );
      })}
    </>
  );
};

export default CustomOptionSelector;
